import {
  Box,
  IconButton,
  InputBase,
  Popover,
  FormControlLabel,
  Switch,
  Typography,
  Skeleton,
  Tooltip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Cookies from 'universal-cookie';
import useFetch from '../../api/useFetch';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import Pagination from '../../components/special/Pagination';
import ExoAPIFilter from '../../components/exo/ExoAPIFilter';
import ProjectControllingCard from './ProjectControllingCard';
import ProjectControllingDetailsCard from './ProjectControllingDetailsCard';
import useControllingPreferences, {
  metricOptions,
} from './useControllingPreferences';

export const ProjectControllingList = ({ projectStatus }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const apiStatusFilter =
    projectStatus === 'active' ? 'status[eq]=0' : 'status[ne]=0';

  const FILTER_COOKIE_KEY = 'project-controlling-filters';

  // Read persisted filter from cookie/sessionStorage so the initial load
  // matches whatever filter the user previously applied.
  function readPersistedFilters() {
    const cookies = new Cookies();
    const cookieValue = cookies.get(FILTER_COOKIE_KEY);
    const sessionRaw = sessionStorage.getItem(FILTER_COOKIE_KEY);
    if (!cookieValue || !sessionRaw) return null;
    try {
      const filters = JSON.parse(sessionRaw);
      return { filters, string: cookieValue };
    } catch {
      return null;
    }
  }

  // Freeze the persisted filter at mount. Filter/sort submits write a new value
  // to the cookie; if we re-read it on every render the useFetch path below would
  // change and fire a duplicate request on top of the imperative fetchProject call.
  // Reading it only once keeps useFetch as a pure initial load (plus apiStatusFilter
  // toggle), while live updates flow exclusively through fetchProject.
  const [initialFilters] = useState(() => readPersistedFilters() || {});
  const initialFilterString = [apiStatusFilter, initialFilters.string]
    .filter(Boolean)
    .join('&');

  // Main paginated project list data.
  const { apiData, setApiData, isLoading } = useFetch(
    `projects/controlling?${initialFilterString}`,
  );

  const [loadingState, setLoadingState] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [currentFilters, setCurrentFilters] = useState(initialFilters);
  const [isExporting, setIsExporting] = useState(false);

  // Search state
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef(null);

  // Accumulated data is fetched separately
  const [accumulatedData, setAccumulatedData] = useState(null);
  const [accumulatedLoading, setAccumulatedLoading] = useState(false);

  const { prefs, toggleMetric } = useControllingPreferences();

  // Metric visibility popover
  const [anchorEl, setAnchorEl] = useState(null);

  const filterConfig = {
    cookieKey: FILTER_COOKIE_KEY,
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'toggle',
          label: t('Has Open Billable'),
          key: 'hasOpenBillable',
          baseValue: 'openBillableTimeNetto[gt]=0',
          active: false,
        },
      ],
    },
    order: {
      label: t('Sort by'),
      items: [
        { label: t('Name'), value: 'name', active: false },
        {
          label: t('Open Billable'),
          value: 'openBillableTimeNetto',
          active: false,
        },
        {
          label: t('Order Volume'),
          value: 'orderedSumNetto',
          active: false,
        },
        {
          label: t('Budget Deviation'),
          value: 'budgetDeviation',
          active: false,
        },
        {
          label: t('Work Progress'),
          value: 'workPackageProgress',
          active: false,
        },
        { label: t('Deadline'), value: 'deadline', active: false },
      ],
    },
  };

  function getControllingFilterString(
    changedFilter = currentFilters,
    search = searchTerm,
  ) {
    const params = [apiStatusFilter];

    // Add ExoAPIFilter query params such as toggle filters, sorting and search.
    if (changedFilter?.string) {
      params.push(changedFilter.string);
    }

    if (search) {
      params.push(`projectName[like]=${encodeURIComponent(search)}`); //safe URL format
    }

    return params.filter(Boolean).join('&');
  }

  function fetchProject(changedFilter = currentFilters, search = searchTerm) {
    const filterString = getControllingFilterString(changedFilter, search);

    // Fetch paginated project list using the same filter string.
    DBRequest({
      config,
      path: `projects/controlling?${filterString}`,
      method: API_GET,
      onResponse: setApiData,
      onLoading: setLoadingState,
    });
  }

  function fetchAccumulatedProjects(changedFilter = currentFilters) {
    // Accumulated totals ignore the search term on purpose — the overview
    // should always reflect the filtered project set, not the searched subset.
    const filterString = getControllingFilterString(changedFilter, '');

    // Fetch accumulated totals using the same filters.
    DBRequest({
      config,
      path: `projects/controlling/accumulated?${filterString}`,
      method: API_GET,
      onResponse: setAccumulatedData,
      onLoading: setAccumulatedLoading,
    });
  }

  function handleFilterChange(changedFilter) {
    const nextFilters = changedFilter || {};

    // Store current filters so pagination and export can reuse them.
    setCurrentFilters(nextFilters);

    // Update both list and  overview with the same filters.
    fetchProject(nextFilters);
    fetchAccumulatedProjects(nextFilters);
  }

  // Debounced refetch when the search term changes.
  const isFirstSearchRunRef = useRef(true);
  useEffect(() => {
    if (!config) return;
    if (isFirstSearchRunRef.current) {
      isFirstSearchRunRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchProject(currentFilters, searchTerm);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!config) return;
    // Load accumulated totals initially and when switching active/inactive projects.
    // Respect any persisted filter from the cookie so the overview matches the list.
    DBRequest({
      config,
      path: `projects/controlling/accumulated?${getControllingFilterString(currentFilters, '')}`,
      method: API_GET,
      onResponse: setAccumulatedData,
      onLoading: setAccumulatedLoading,
    });
  }, [apiStatusFilter, config]);

  const handleExport = async () => {
    setIsExporting(true);
    const cookies = new Cookies();
    const params = new URLSearchParams();

    // Export should respect the current project status.
    if (projectStatus === 'active') {
      params.append('status[eq]', '0');
    } else {
      params.append('status[ne]', '0');
    }

    // Export should also respect the currently selected filters.
    if (currentFilters.string) {
      currentFilters.string.split('&').forEach((part) => {
        const [key, value] = part.split('=');

        if (key && value) {
          params.append(key, value);
        }
      });
    }

    // Include the active search term (project name LIKE filter).
    if (searchTerm) {
      params.append('projectName[like]', searchTerm);
    }

    try {
      const response = await fetch(
        `${config.API_URL}export-project-controlling?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${cookies.get('accessToken')}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `project_controlling_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const showLoading = isLoading || loadingState;
  const projects = apiData?.data || [];

  // Pagination must receive the same active filters as the main list API.
  const paginationFilter = {
    filter: getControllingFilterString(currentFilters),
  };

  return (
    <Box className="flex flex-col gap-2 h-full overflow-hidden">
      {/* Toolbar */}
      <Box className="flex items-center gap-1 justify-between">
        {/* Animated Search */}
        <Box
          className="flex items-center overflow-hidden transition-all duration-300"
          sx={{
            width: searchExpanded ? '260px' : '32px',
            minWidth: '32px',
            bgcolor: searchExpanded ? colors.bgInput : colors.card,
            borderRadius: '8px',
            border: searchExpanded
              ? `1px solid ${colors.info}`
              : '1px solid transparent',
            '&:hover': {
              bgcolor: searchExpanded ? colors.bgInput : colors.selected,
            },
          }}
        >
          {searchExpanded ? (
            <>
              <SearchIcon sx={{ fontSize: '18px', ml: 1, opacity: 0.5 }} />
              <InputBase
                placeholder={t('Search projects...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchExpanded(false);
                    setSearchTerm('');
                  }
                }}
                size="small"
                className="flex-1 px-2"
                autoFocus
                sx={{ fontSize: '13px' }}
                inputProps={{
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: false,
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setSearchExpanded(false);
                  setSearchTerm('');
                }}
              >
                <ClearIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </>
          ) : (
            <IconButton
              size="small"
              onClick={() => setSearchExpanded(true)}
              sx={{ width: '100%', borderRadius: '8px' }}
            >
              <SearchIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>

        <Box className="flex items-center gap-1">
          <ExoAPIFilter
            filters={filterConfig}
            onChange={handleFilterChange}
            currentFilters={currentFilters}
          />

          <Tooltip title={t('CSV exportieren')}>
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <ViewColumnIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box className="p-3 flex flex-col gap-1" sx={{ minWidth: 250 }}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                className="mb-1"
              >
                {t('Display Settings')}
              </Typography>
              {metricOptions.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  control={
                    <Switch
                      size="small"
                      checked={prefs.visibleMetrics.includes(opt.key)}
                      onChange={() => toggleMetric(opt.key)}
                    />
                  }
                  label={
                    <Typography variant="body2">{t(opt.labelKey)}</Typography>
                  }
                />
              ))}
            </Box>
          </Popover>
        </Box>
      </Box>
      {/* Accumulated totals for all projects matching current filters */}
      <ProjectControllingDetailsCard
        data={accumulatedData?.data || accumulatedData}
        visibleMetrics={prefs.visibleMetrics}
        isLoading={accumulatedLoading}
      />

      {/* Card List */}
      <Box className="flex flex-col gap-2 overflow-y-auto flex-1">
        {showLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={100}
              sx={{ borderRadius: '12px' }}
            />
          ))
        ) : projects.length === 0 ? (
          <Box className="flex justify-center items-center py-8">
            <Typography color="text.secondary">
              {t('No projects found')}
            </Typography>
          </Box>
        ) : (
          projects.map((project) => (
            <ProjectControllingCard
              key={project.id}
              project={project}
              visibleMetrics={prefs.visibleMetrics}
              isExpanded={expandedId === project.id}
              onToggleExpand={() =>
                setExpandedId(expandedId === project.id ? null : project.id)
              }
            />
          ))
        )}
      </Box>

      {/* Pagination */}
      {apiData && (
        <Box
          className="backdrop-blur-lg rounded-lg p-2"
          sx={{ backgroundColor: colors.glass }}
        >
          <Pagination
            data={apiData}
            setData={setApiData}
            setLoading={setLoadingState}
            filter={paginationFilter}
          />
        </Box>
      )}
    </Box>
  );
};
